const normalizeText = (value = "") => String(value ?? "").trim();

const EMAIL_NOTE_ACTIONS = new Set([
  "inbound-project-email",
  "supplier-email-sent",
  "sent-email",
  "estimate-email-sent",
  "sales-doc-email-sent",
  "presentation-sent",
  "presentation-resent",
  "presentation-email-simulated",
  "presentation-email-resend-simulated",
]);

const splitRecipients = (value) =>
  String(value || "")
    .split(",")
    .map((entry) => normalizeText(entry))
    .filter(Boolean);

const decodeHtmlEntities = (value = "") => {
  if (!normalizeText(value)) return "";
  if (typeof document !== "undefined") {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = String(value);
    return textarea.value;
  }
  return String(value)
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&#39;/gi, "'")
    .replace(/&quot;/gi, '"');
};

export const htmlToPlainTextPreservingBreaks = (html = "") =>
  decodeHtmlEntities(
    String(html || "")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n\n")
      .replace(/<\/div>/gi, "\n")
      .replace(/<\/li>/gi, "\n")
      .replace(/<li>/gi, "* ")
      .replace(/<[^>]+>/g, " ")
      .replace(/\r/g, "")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n[ \t]+/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
  ).trim();

const parseDisplayNameAndEmail = (value = "") => {
  const raw = normalizeText(value);
  const match = raw.match(/^(.*?)(?:<([^>]+)>)$/);
  if (!match) {
    return { fromName: "", fromEmail: raw };
  }
  return {
    fromName: normalizeText(match[1].replace(/["']/g, "")),
    fromEmail: normalizeText(match[2]),
  };
};

const extractFieldValue = (message = "", label = "") => {
  const lineFriendlyMessage = String(message || "").replace(/<br\s*\/?>/gi, "\n");
  const regex = new RegExp(`(?:^|\\n)${label}:\\s*([^\\n]+)`, "i");
  const rawValue = lineFriendlyMessage.match(regex)?.[1] || "";
  return normalizeText(decodeHtmlEntities(rawValue.replace(/<\/(?:p|div)>/gi, "")));
};

const extractDetailsBody = (message = "") => {
  const match = String(message || "").match(/<details>\s*<summary>Email copy<\/summary>\s*<div[^>]*>([\s\S]*?)<\/div>\s*<\/details>/i);
  return match ? match[1] : "";
};

const extractBlockquoteBody = (message = "") => {
  const match = String(message || "").match(/<blockquote[^>]*>([\s\S]*?)<\/blockquote>/i);
  return match ? match[1] : "";
};

const buildStructuredEmail = (note = {}) => {
  const email = note?.email || note?.meta?.email;
  if (!email || typeof email !== "object") return null;

  return {
    layout: email.layout || "plain",
    fromName: email.fromName ?? note.fromName ?? note.userName ?? "",
    fromEmail: email.fromEmail ?? note.fromEmail ?? note.userEmail ?? "",
    to: email.to ?? email.recipients ?? (note.originalRecipient ? [note.originalRecipient] : []),
    cc: email.cc ?? [],
    bcc: email.bcc ?? [],
    subject: email.subject ?? extractFieldValue(note.message, "Subject") ?? "",
    htmlBody: email.htmlBody ?? "",
    textBody: email.textBody ?? "",
    fullHtmlBody: email.fullHtmlBody ?? "",
    fullTextBody: email.fullTextBody ?? "",
    messageMode: email.messageMode ?? "",
    forwardedMessage: email.forwardedMessage ?? null,
    sentAt: email.sentAt ?? "",
    receivedAt: email.receivedAt ?? note.receivedAt ?? "",
    attachments: Array.isArray(email.attachments) && email.attachments.length
      ? email.attachments
      : (Array.isArray(note.attachments) ? note.attachments : []),
  };
};

const buildLegacyInboundEmail = (note = {}) => {
  const message = String(note.message || "");
  const subject = extractFieldValue(message, "Subject") || "(No Subject)";
  const fromLine = extractFieldValue(message, "From") || normalizeText(note.fromEmail);
  const { fromName, fromEmail } = parseDisplayNameAndEmail(fromLine);
  const lines = String(message || "").replace(/\r/g, "").split("\n");
  const bodyStart = lines.findIndex((line) => /^From:/i.test(line)) + 1;
  const tailStart = lines.findIndex((line) => /^Sender status:/i.test(line) || /^Attachments:/i.test(line));
  const bodyLines = lines.slice(
    bodyStart > 0 ? bodyStart : 0,
    tailStart >= 0 ? tailStart : lines.length
  );
  const textBody = normalizeText(bodyLines.join("\n").trim()) || htmlToPlainTextPreservingBreaks(message);

  return {
    layout: "plain",
    fromName: fromName || note.fromName || "",
    fromEmail: fromEmail || note.fromEmail || "",
    to: note.originalRecipient ? [note.originalRecipient] : [],
    cc: [],
    bcc: [],
    subject,
    htmlBody: "",
    textBody,
    receivedAt: note.receivedAt || "",
    attachments: Array.isArray(note.attachments) ? note.attachments : [],
  };
};

const buildLegacySentEmail = (note = {}) => {
  const message = String(note.message || "");
  const detailsBody = extractDetailsBody(message);
  const blockquoteBody = extractBlockquoteBody(message);
  const subject = extractFieldValue(message, "Subject") || "(No Subject)";
  const to = splitRecipients(extractFieldValue(message, "To"));
  const cc = splitRecipients(extractFieldValue(message, "CC"));
  const bcc = splitRecipients(extractFieldValue(message, "BCC"));
  const fromNarration = htmlToPlainTextPreservingBreaks(message).match(/^(.*?) sent email to /i)?.[1] || "";

  return {
    layout: "plain",
    fromName: normalizeText(fromNarration || note.userName || ""),
    fromEmail: normalizeText(note.userEmail || ""),
    to,
    cc,
    bcc,
    subject,
    htmlBody: detailsBody || blockquoteBody || "",
    textBody: htmlToPlainTextPreservingBreaks(detailsBody || blockquoteBody || message),
    sentAt: note.createdAt || note.timestamp || "",
    attachments: Array.isArray(note.attachments) ? note.attachments : [],
  };
};

export const canViewEmailNote = (note = {}) => {
  if (buildStructuredEmail(note)) return true;
  if (String(note?.noteType || "").toLowerCase() === "inbound-email") return true;
  if (EMAIL_NOTE_ACTIONS.has(String(note?.action || "").trim().toLowerCase())) return true;
  return false;
};

export const buildEmailViewerPropsFromNote = (note = {}) => {
  const structured = buildStructuredEmail(note);
  if (structured) {
    const fallback = String(note?.noteType || "").toLowerCase() === "inbound-email"
      ? buildLegacyInboundEmail(note)
      : buildLegacySentEmail(note);
    return {
      ...fallback,
      ...structured,
      textBody: structured.textBody || fallback.textBody,
      htmlBody: structured.htmlBody || fallback.htmlBody,
      subject: structured.subject || fallback.subject,
      to: Array.isArray(structured.to) && structured.to.length ? structured.to : fallback.to,
      cc: Array.isArray(structured.cc) ? structured.cc : fallback.cc,
      bcc: Array.isArray(structured.bcc) ? structured.bcc : fallback.bcc,
      attachments: Array.isArray(structured.attachments) ? structured.attachments : fallback.attachments,
    };
  }

  const action = String(note?.action || "").trim().toLowerCase();
  if (String(note?.noteType || "").toLowerCase() === "inbound-email" || action === "inbound-project-email") {
    return buildLegacyInboundEmail(note);
  }
  if (EMAIL_NOTE_ACTIONS.has(action)) {
    return buildLegacySentEmail(note);
  }
  return null;
};

const PREVIEW_MAX_LENGTH = 160;

const emailPartyDisplayName = (value = "") => {
  const { fromName, fromEmail } = parseDisplayNameAndEmail(value);
  return fromName || fromEmail || "";
};

const cleanEmailPreviewBody = (value = "") => {
  const lines = htmlToPlainTextPreservingBreaks(value)
    .replace(/\r/g, "")
    .split("\n");
  const kept = [];

  for (const rawLine of lines) {
    const line = normalizeText(rawLine);
    if (/^(?:On .+ wrote:|From:\s|Sent:\s|To:\s|Subject:\s|_{5,}|-{5,})/i.test(line)) break;
    if (/^(?:--\s*|best(?: regards)?[,!]?|kind regards[,!]?|regards[,!]?|sincerely[,!]?|thanks[,!]?|thank you[,!]?|cheers[,!]?|sent from my )$/i.test(line)) break;
    if (/^(?:sender status|attachments?):/i.test(line)) continue;

    const withoutUrls = line
      .replace(/\[?https?:\/\/(?:lh\d+-[\w-]+\.)?googleusercontent\.com\/[^\s\]]+\]?/gi, " ")
      .replace(/^\[?https?:\/\/[^\s\]]+\]?$/gi, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (withoutUrls) kept.push(withoutUrls);
  }

  return kept.join(" ").replace(/\s+/g, " ").trim();
};

const truncatePreview = (value, maxLength = PREVIEW_MAX_LENGTH) => {
  const text = normalizeText(value);
  if (text.length <= maxLength) return text;
  const candidate = text.slice(0, maxLength - 1).trimEnd();
  const lastSpace = candidate.lastIndexOf(" ");
  const shortened = lastSpace >= Math.floor(maxLength * 0.65)
    ? candidate.slice(0, lastSpace)
    : candidate;
  return `${shortened.trimEnd()}…`;
};

export const buildEmailNotePreview = (note = {}) => {
  if (!canViewEmailNote(note)) return null;
  const email = buildEmailViewerPropsFromNote(note);
  if (!email) return null;

  const action = String(note?.action || "").trim().toLowerCase();
  const inbound = String(note?.noteType || "").toLowerCase() === "inbound-email"
    || action === "inbound-project-email";
  const recipients = Array.isArray(email.to) ? email.to : splitRecipients(email.to);
  const party = inbound
    ? (email.fromName || emailPartyDisplayName(email.fromEmail))
    : emailPartyDisplayName(recipients[0]);
  const bodySource = email.textBody || htmlToPlainTextPreservingBreaks(email.htmlBody);

  return {
    directionLabel: inbound ? "Email received" : "Email sent",
    party,
    subject: normalizeText(email.subject) || "(No Subject)",
    bodyPreview: truncatePreview(cleanEmailPreviewBody(bodySource)),
    attachmentCount: Array.isArray(email.attachments) ? email.attachments.length : 0,
    canViewEmail: true,
  };
};
